/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, Input, OnInit, ViewChild} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {NbAutocompleteDirective, NbInputModule} from '@nebular/theme';
import {BaseComponent, CustomerGroup, FileService, ProductCustomer, StoreService} from 'compob2b-libs';
import {debounceTime, distinctUntilChanged, take, takeUntil, tap} from 'rxjs/operators';
import {ProductService} from '../product/services/product.service';
import {IS_SHOWCASE} from '../shared/tokens/is-showcase.token';
import {ShowcaseContextService} from '../utils/showcase-context.service';
import {catalogIdFromPath} from '../shared/routing/url-builder';
import {ProductAvailabilityComponent} from '../product/product-list/product-availability/product-availability.component'
import {ProductPriceComponent} from '../product/product-list/product-price/product-price.component'
import {AddToCartComponent} from '../cart/add-to-cart/add-to-cart.component'
import {TuiIcon} from '@taiga-ui/core'
import {AddToSpecificationComponent} from '../cart/add-to-specification/add-to-specification.component'

@Component({
  selector: 'app-search-autocomplete',
  templateUrl: './search-autocomplete.component.html',
  styleUrl: './search-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProductAvailabilityComponent,
    ProductPriceComponent,
    AddToCartComponent,
    RouterLink,
    NbInputModule,
    TuiIcon,
    ReactiveFormsModule,
    AddToSpecificationComponent
  ]
})

export class SearchAutocompleteComponent extends BaseComponent implements OnInit {
  @Input() mode = 'search';
  @ViewChild(NbAutocompleteDirective) autoComplete: NbAutocompleteDirective<any>;
  @ViewChild('autoCompleteInput') autoCompleteInput: ElementRef<HTMLElement>;
  readonly customerGroup$ = this.storeService.getCustomerGroup();

  productService = inject(ProductService);

  inputFormControl: FormControl;
  visible = false;
  terms: ProductCustomer[] = [];
  isShowcase = inject(IS_SHOWCASE);
  showcaseCtx = inject(ShowcaseContextService);

  constructor(
    private readonly storeService: StoreService,
    protected readonly route: ActivatedRoute,
    private readonly fileService: FileService,
    private readonly elementRef: ElementRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.inputFormControl = new FormControl();
    this.inputFormControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(300),
        distinctUntilChanged(),
        tap(term => this.searchTerms(term))
      ).subscribe();

    this.route.queryParamMap
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(param => {
        this.inputFormControl.patchValue(param.get('keyword'));
        this.cdr.detectChanges();
      });

    this.customerGroup$
      .pipe(
        tap(data => this.handleCustomerGroup(data)),
        takeUntil(this.unsubscribe)
      ).subscribe();
  }

  show() {
    this.visible = true;
    this.cdr.detectChanges();
  }

  hide() {
    this.terms = [];
    this.visible = false;
    this.cdr.detectChanges();
  }

  openModalProduct(product: ProductCustomer) {
    if (!this.isSpecification) {
      this.hide();
      this.productService.openModalProductLazy(product.syncUid);
    }
  }

  public getImageUri(product: ProductCustomer): string {
    return this.fileService.getProductThumbnailSrc(product, 200, this.showcaseCtx.hash) ?? '/assets/images/product/nofoto.png';
  }

  searchTerms(term: string): void {
    if (!term || term.length < 2 || !this.visible) {
      this.hide();
    } else {
      // Категория уехала из query в path (ТЗ §7), а этот компонент живёт в
      // лейауте — path-параметра каталожного роута у него нет. Читаем из URL.
      // Для бэка `catalog_id` остаётся query-параметром API: 0 = искать везде.
      const catalogId = catalogIdFromPath(this.router.url);
      let params = new HttpParams().set('keyword', term).set('catalog_id', String(catalogId ?? 0));
      if (this.productService.customerGroup) {
        params = params.set('region', this.productService.customerGroup.id);
      }

      params = params.appendAll({
        order: 'availability',
        direction: 'desc'
      });

      this.compoApiService
        .getWithParams<ProductCustomer[]>('/product/search-autocomplete', params)
        .pipe(
          take(1),
          tap(data => {
            this.terms = data.data;
            this.terms && this.visible ? this.show() : this.hide();
          })
        ).subscribe();
    }
  }

  @HostListener('document:keydown.escape')
  @HostListener('window:popstate', ['$event'])
  onHide(): void {
    this.hide();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: any): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.hide();
    } else {
      this.show();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown($event: KeyboardEvent) {
    if ($event.keyCode == 13) {
      this.onSubmit();
    } else if ($event.keyCode == 27) {
      this.hide();
    } else {
      this.show();
    }
  }

  get value() {
    return this.inputFormControl.value;
  }

  get isSpecification() {
    return this.mode == 'specification';
  }

  get isSearch() {
    return this.mode == 'search';
  }

  onSubmit() {
    if (this.isSearch) {
      this.hide();
      this.router.navigate(['/catalog'], {
        queryParams: {keyword: this.value, page: 1},
        queryParamsHandling: 'merge'
      });
    }
  }

  private handleCustomerGroup(customerGroup: CustomerGroup) {
    this.productService.handleCustomerGroup(customerGroup);
    this.cdr.detectChanges();
  }
}
